/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import deepcopy from 'deepcopy';
import isNil from 'lodash.isnil';
import isObject from 'lodash.isobject';
import isObjectLike from 'lodash.isobjectlike';
import isUndefined from 'lodash.isundefined';
import {GenericObject} from '../types/entities';

export type Mapped = {
  [key: string]: any
}

/**
 * @objectKeyLength
 *
 * Given a an object, returns the length of the object keys
 *
 * ```typescript
 *   import {objectKeyLength} from '@ion/core/src/utils/';
 *
 *   objectKeyLength({}); // 0
 *   objectKeyLength(null); // 0
 *   objectKeyLength({id: 0}); // 1
 *   objectKeyLength({id: 0, name: 'Ryan'}); // 2
 *
 * ```
 *
 * @method   isEmpty
 * @param    {GenericObject} obj The object which is to be inspected.
 * @returns  {boolean} True if the obj is not empty, otherwise, false.
 */
export const objectKeyLength = (obj: GenericObject): number => {
  return obj && typeof obj === 'object' ? Object.keys(obj).length : 0;
};

/**
 * @isObjectEmpty
 *
 * Given a an object, determines if the object is empty (i.e. does not have any properties)
 * or not.
 *
 * ```typescript
 *   import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
 *
 *   isObjectEmpty({}); // true
 *   isObjectEmpty(null); // true
 *   isObjectEmpty({id: 0}); // false
 *
 * ```
 *
 * @method   isEmpty
 * @param    {GenericObject} obj The object which is to be inspected.
 * @returns  {boolean} True if the obj is not empty, otherwise, false.
 */
export const isObjectEmpty = (obj: GenericObject): boolean => {
  return obj ? Boolean(Object.keys(obj).length === 0) : true;
};

/**
 * @idMap
 *
 * Given a list of objects and an optional property (defaults to 'id'), returns
 * a map where each item's property is used as the key and the value is the item.
 *
 * ```typescript
 *   import {idMap} from '@ion/core/src/utils/ObjectUtil';
 *
 *   const items = [{id: 0}, {id: 1}, {id: 2}];
 *   const map   = idMap(items);
 *   Object.keys(map).length // 3
 *   map[0] === items[0] // true
 *   map[1] === items[1] // true
 *   map[2] === items[2] // true
 *
 * ```
 *
 * @method idMap
 */
export const idMap = (items: any[], property = 'id', valProp: any = null): Mapped => {
  const map = {};
  items?.forEach(item => {
    const value = item[property];
    if (!isNil(value)) {
      map[value] = valProp ? item[valProp] : item;
    }
  });
  return map;
};

/**
 * @idMap
 *
 * Given a list of objects and an optional property (defaults to 'id'), returns
 * an array of properties from the items in the list.
 *
 * ```typescript
 *   import {toProperties} from '@ion/core/src/utils/';
 *
 *   const items = [{id: 100, name: 'Item 100'}, {id: 101, name: 'Item 101'}];
 *   const ids   = toProperties(items);
 *   items.length // 2
 *   map[0] === 100 // true
 *   map[1] === 101 // true
 *
 * ```
 *
 * @method idMap
 */
export const toProperties = (items: any[], property = 'id'): any[] => {
  return items.map(item => item[property]);
};

/**
 * @deepCopy
 *
 * Provides a clone implementation which performs a deep copy of a source object
 * to a new, exact replicated copy of the source.
 *
 * ```typescript
 *   import {deepCopy} from '@ion/core/src/utils/ObjectUtil';
 *
 *   const source = [{id: 100, name: 'Item 100'}, {id: 101, name: 'Item 101'}];
 *   const copy   = deepCopy(source);
 *   const isSame = source === copy; // false;
 *
 *   source[0].id = 200;
 *   source[0].id; // 200
 *   copy[0].id; // 100
 *
 * ```
 *
 * @param source The source to be cloned
 */
export const deepCopy = (source: any): any => {
  return deepcopy(source);
};

/**
 * Provides a semantic alias to the deepCopy function
 *
 * @see deepCopy
 */
export const clone = deepCopy;

/**
 * @toMap
 *
 * Converts an array to an object, where each object key is a property of the
 * object in the array, or, if the array element is a simple type, the element
 * is used as both the key and value, unless the defaultValue argument is set,
 * in which case the element is used as the key, and the defaultValue is used
 * as the value.
 *
 * ```typescript
 *   import {toMap} from '@ion/core/src/utils/ObjectUtil';
 *
 *   const items = [{id: 10, name: 'larry'}, {id: 100, name: 'moe'}];
 *   const map = toMap(items, 'name');
 *
 *   Object.keys(map).length // 2
 *   map.larry  // items[0]
 *   map.moe    // items[1]
 *
 * ```
 *
 */
export const toMap = (items: any[], property: string = null, defaultValue = null): Mapped => {
  const map = {};
  items.forEach(item => {
    const key = isObjectLike(item) ? item[property] : item;
    map[key]  = defaultValue || item;
  });
  return map;
};

/**
 * @removeUndefined
 *
 * Provides a utility which removes any keys which have undefined values.
 *
 * ```typescript
 *   import {removeUndefined} from '@ion/core/src/utils/';
 *   removeUndefined({id: 10, name: undefined}); // {id: 10}
 *
 * ```
 */
export const removeUndefined = (obj: object): object => {
  Object.keys(obj).forEach(key => {
    if (typeof obj[key] === 'undefined') {
      delete obj[key];
    }
  });
  return obj;
};

/**
 * @getQueryString
 *
 * Converts a given object to a parameter query string.
 *
 * ```typescript
 *   import {getQueryString} from '@ion/core/src/utils/QueryStringUtils';
 *
 *   const queryParam = {f: 'json', text: 'Los Angeles', maxSuggestions: 6};
 *   const queryString = getQueryString(queryParam);
 *   console.log(queryString); // "f=json&text=Los%20Angeles&maxSuggestions=6"
 *
 * ```
 *
 */
export const getQueryString = (queryParam: object) => {
  return Object.keys(queryParam).map((key) => {
    return `${encodeURIComponent(key)}=${encodeURIComponent(queryParam[key])}`;
  }).join('&');
};

/**
 * @removeDuplicate
 *
 * Provides a utility which removes any Duplicate object from the given array.
 *
 * ```typescript
 *   import {removeDuplicate} from "@ion/core/src/utils";
 *   const array=[{id:1,label:"One"},{id:2,label:"Two"},{id:1,label:"One"}];
 *   removeDuplicate(id, array); // [{id:1,label:"One"},{id:2,label:"Two"}]
 *
 * ```
 */
export function removeDuplicate<T>(key: string, array: Array<T>, includeNil = true): Array<T> {
  const setObj = new Set();
  const newArrayWithoutDuplicate = array?.reduce((acc, item) => {
    if (!setObj.has(item[key])) {
      (includeNil || item[key]) && setObj.add(item[key]);
      acc.push(item);
    }
    return acc;
  }, []);
  return newArrayWithoutDuplicate;
}

/**
 * @removeElFromArray
 *
 * Simple utility that removes any item from an array. Uses the items
 * index to splice it from a shallow copy of the array, returning the
 * shallow copy.
 *
 * ```typescript
 *   import {removeElFromArray} from "@ion/core/src/utils";
 *
 *   const updated = removeElFromArray([1, 2, 3, 4, 5], 4)
 *   // [1, 2, 3, 5]
 *
 * ```
 *
 */
export function removeElFromArray(arr: any[], el: any) {
  const arrCopy = arr.slice(0);
  const self = arrCopy.indexOf(el);
  arrCopy.splice(self, 1);
  return arrCopy;
};

/**
 * @hasValue
 *
 * Given an object, determines if any one of the object's property
 * has value or not.
 *
 * ```typescript
 *   import {hasValue} from '@ion/core/src/utils/';
 *
 *   const item = {name: 'Asif', id: null, status: undefined};
 *   const result = hasValue(item);
 *   console.log(result); // true
 *
 *   const item1 = {name: '', id: null, status: false};
 *   const result = hasValue(item1);
 *   console.log(result); // true
 *
 *   const item2 = {name: '', id: null, status: undefined};
 *   const result = hasValue(item2);
 *   console.log(result); // false
 *
 * ```
 *
 * @method  hasValue
 * @param   {GenericObject} obj The object which is to be inspected.
 * @returns {boolean} True if any one of the obj's property has value,
 *           otherwise, false.
 */
export const hasValue = (obj: GenericObject, prop = 'value'): boolean => {
  let hasValue = false;
  Object.keys(obj)?.forEach(key => {
    const val = obj[key]?.[prop];
    if (!isUndefined(val) && val !== '') {
      hasValue = true;
      return hasValue;
    }
  });
  return hasValue;
};

/**
 * The utility function provides a simple utility that sorts the given object by its property name/key
 * in ascending or descending order. This sorting is at a deep level.
 *
 * ```typescript
 *
 *  import {sortByKey} from '@ion/core/src/utils/';
 *
 *  const ascOrdered  = sortByKey({b: 1, c: 2, a: 3}); // {a: 3, b: 1, c: 2}
 *  const dscOrdered  = sortByKey({b: 1, c: 2, a: 3}, false); // {c: 2, b: 1, a: 3}
 *  const deepOrdered = sortByKey({b: 1, c: {l: 5, k: 2}, a: 3}); // {a: 3, b: 1, c: {k: 2, l: 5}}
 *
 * ```
 */
export const sortByKey = (unordered: GenericObject, ascending = true): GenericObject => {
  return Object.entries(unordered)
    .sort(([key1]: [string, any], [key2]: [string, any]) => (
      (ascending ? key1.localeCompare(key2) : key2.localeCompare(key1))
    ))
    .reduce((res, [key, val]) => {
      res[key] = isObject(val) ? sortByKey(val, ascending) : val;
      return res;
    }, {});
};

/**
 * Return a key of object, if the value of object specified.
 *
 * ```typescript
 *   import {getKeyByValue} from '@ion/core/src/utils/';
 *
 *   getKeyByValue({a:1, b:2}, 1); // a
 *
 * ```
 *
 * @param    {GenericObject} obj The object which is to be inspected.
 * @param    {value} string The value which is to be inspected.
 * @returns  {string} key of specified value in the object.
 */
export const getKeyByValue = (obj: GenericObject, value: string | number) => (
  Object.keys(obj).find(key => obj[key] === value)
);

/**
 * Provides a utility which determines if the given value is an explicit object
 * instance, that is, the value is of type object, is not an array, and is non
 * nil.
 *
 * ```typescript
 *   import {isExplicitObject} from '@ion/core/src/utils/';
 *
 *   isExplicitObject({}); // true
 *   isExplicitObject([]); // false
 *   isExplicitObject(null); // false
 *
 * ```
 *
 * @param   The val which is to be validates as being of explicit type object.
 * @returns `true` if the given value is an object, otherwise, `false`.
 */
export const isExplicitObject = (val: any): boolean => (
  val && typeof val === 'object' && !Array.isArray(val)
);

/**
 * The utility function provides a simple utility that returns a new object
 * from a given object and a given array of keys.
 *
 * ```typescript
 *   import {filterObjectByKeys} from '@ion/core/src/utils/';
 *
 *   const obj = {firstName: 'Shahid', lastName: 'Rahim', age: 29, gender: 'Male'};
 *   const arr = ['firstName', 'age'];
 *
 *   filterObjectByKeys(obj, arr); // {firstName: 'Shahid', age: 29};
 *
 * ```
 */
export const filterObjectByKeys = (obj: GenericObject, allowed: string[]): GenericObject => {
  return Object.keys(obj)
    .filter((key) => allowed.includes(key))
    .reduce((res, key) => {
      res[key] = obj[key];
      return res;
    }, {});
};
